import moment from 'moment';
import Color from 'color';
import { ROLE_TYPES } from './DataConstants';
import { COMPARISON_PERIOD_COLOR } from '../components/life_cycle_analytics_overview_page/LifeCycleAnalyticsConstants';

/** Ant Design @primary-color from config-overrides.js (buyer / supplier builds). */
const BUYER_ROLE_PRIMARY_COLOR = '#6027b0';
const SUPPLIER_ROLE_PRIMARY_COLOR = '#2792b0';

export { COMPARISON_PERIOD_COLOR };

export const getAnalyticsRolePrimaryColor = (userType: string | null | undefined): string => {
	if (userType === ROLE_TYPES.SUPPLIER) {
		return SUPPLIER_ROLE_PRIMARY_COLOR;
	}
	return BUYER_ROLE_PRIMARY_COLOR;
};

export const COMPARISON_MODE = {
	NONE: 'none',
	PREVIOUS_PERIOD: 'previous_period',
	LAST_YEAR: 'last_year',
	CUSTOM: 'custom',
} as const;

export type ComparisonMode = typeof COMPARISON_MODE[keyof typeof COMPARISON_MODE];

export interface ComparisonState {
	mode: ComparisonMode;
	customStart?: moment.Moment | null;
	customEnd?: moment.Moment | null;
}

export const DEFAULT_COMPARISON_STATE: ComparisonState = {
	mode: COMPARISON_MODE.NONE,
	customStart: null,
	customEnd: null,
};

export const COMPARISON_MODE_LABELS: Record<ComparisonMode, string> = {
	[COMPARISON_MODE.NONE]: 'No Comparison',
	[COMPARISON_MODE.PREVIOUS_PERIOD]: 'Previous Period',
	[COMPARISON_MODE.LAST_YEAR]: 'This time last year',
	[COMPARISON_MODE.CUSTOM]: 'Custom Range',
};

/** Window of equal duration ending one day before `start`. */
export const getPreviousPeriodRange = (
	start: moment.Moment,
	end: moment.Moment
): [moment.Moment, moment.Moment] => {
	const days = end.diff(start, 'days');
	return [start.clone().subtract(days + 1, 'days'), start.clone().subtract(1, 'day')];
};

/** Same calendar dates shifted back exactly one year. */
export const getYearAgoRange = (
	start: moment.Moment,
	end: moment.Moment
): [moment.Moment, moment.Moment] => [
	start.clone().subtract(1, 'year'),
	end.clone().subtract(1, 'year'),
];

/**
 * Custom comparison dates must match CustomRangeModal rules: valid range, not in the future.
 * Used when hydrating from URL params and before issuing comparison fetches.
 */
export const isValidCustomComparisonRange = (start: moment.Moment, end: moment.Moment): boolean => {
	if (!start.isValid() || !end.isValid()) {
		return false;
	}
	const today = moment().endOf('day');
	if (start.isAfter(today) || end.isAfter(today)) {
		return false;
	}
	if (end.isBefore(start)) {
		return false;
	}
	return true;
};

/** Resolve the comparison [start, end] for a selection. null when mode is NONE or custom dates missing. */
export const resolveComparisonRange = (
	selectedStart: moment.Moment | null | undefined,
	selectedEnd: moment.Moment | null | undefined,
	comparison: ComparisonState | null | undefined
): [moment.Moment, moment.Moment] | null => {
	if (!selectedStart || !selectedEnd || !comparison) return null;
	if (comparison.mode === COMPARISON_MODE.NONE) return null;
	if (comparison.mode === COMPARISON_MODE.PREVIOUS_PERIOD) {
		return getPreviousPeriodRange(selectedStart, selectedEnd);
	}
	if (comparison.mode === COMPARISON_MODE.LAST_YEAR) {
		return getYearAgoRange(selectedStart, selectedEnd);
	}
	if (comparison.mode === COMPARISON_MODE.CUSTOM) {
		if (!comparison.customStart || !comparison.customEnd) return null;
		if (!isValidCustomComparisonRange(comparison.customStart, comparison.customEnd)) {
			return null;
		}
		return [comparison.customStart.clone(), comparison.customEnd.clone()];
	}
	return null;
};

/** Auto-fill the END date so duration matches the selected period. */
export const matchDurationFromStart = (
	customStart: moment.Moment,
	selectedStart: moment.Moment,
	selectedEnd: moment.Moment
): [moment.Moment, moment.Moment] => {
	const days = selectedEnd.diff(selectedStart, 'days');
	return [customStart.clone(), customStart.clone().add(days, 'days')];
};

/** Auto-fill the START date so duration matches the selected period. */
export const matchDurationFromEnd = (
	customEnd: moment.Moment,
	selectedStart: moment.Moment,
	selectedEnd: moment.Moment
): [moment.Moment, moment.Moment] => {
	const days = selectedEnd.diff(selectedStart, 'days');
	return [customEnd.clone().subtract(days, 'days'), customEnd.clone()];
};

/** Same hue as `currentColor`, lightened so it reads as visually subordinate. */
export const derivePreviousPeriodColor = (currentColor: string): string => {
	try {
		return Color(currentColor).lighten(0.45).desaturate(0.2).hex();
	} catch (e) {
		return '#BDB3D8';
	}
};

/** "1 Jan, 2026 - 1 Mar, 2026" — used on legend pills. */
export const formatComparisonRangeLabel = (
	start: moment.Moment | null | undefined,
	end: moment.Moment | null | undefined
): string => {
	if (!start || !end) return '';
	return `${start.format('D MMM, YYYY')} - ${end.format('D MMM, YYYY')}`;
};

export const COMPARISON_SERIES_FIELD = 'series';
export const COMPARISON_SERIES_CURRENT = 'current';
export const COMPARISON_SERIES_PREVIOUS = 'previous';

/**
 * Design tokens — keep in lockstep with tailwind.config.js.
 *   Buyer:    purple.6 / purple.3
 *   Supplier: cyan.6   / cyan.3
 * Role is detected from the current URL pathname because the same build is
 * served to both /buyer/... and /supplier/... routes in this app.
 */
const BUYER_COMPARISON_PALETTE = { current: '#722ed1', previous: '#d3adf7' };
const SUPPLIER_COMPARISON_PALETTE = { current: '#236476', previous: '#3596B1' };

export const isSupplierUrlContext = (): boolean => {
	if (typeof window === 'undefined' || !window.location || !window.location.pathname) {
		return false;
	}
	return window.location.pathname.startsWith('/supplier');
};

const getComparisonPalette = () =>
	isSupplierUrlContext() ? SUPPLIER_COMPARISON_PALETTE : BUYER_COMPARISON_PALETTE;

/**
 * Runtime-safe getters — use these instead of module-level constants. The role
 * is detected from window.location.pathname, so anything evaluated at import
 * time would freeze to whatever palette was current when the bundle first
 * loaded (e.g., on a /login page) and never update when the user navigates
 * into /buyer or /supplier.
 */
export const getComparisonCurrentColor = () => getComparisonPalette().current;
export const getComparisonPreviousColor = () => getComparisonPalette().previous;

/** Label for the "Previous Period" pill, varies by comparison mode. */
export const getComparisonSeriesLabel = (mode: ComparisonMode): string => {
	switch (mode) {
		case COMPARISON_MODE.LAST_YEAR:
			return 'This time last year';
		case COMPARISON_MODE.CUSTOM:
			return 'Custom Range';
		case COMPARISON_MODE.PREVIOUS_PERIOD:
		default:
			return 'Previous Period';
	}
};
